import googleSvg from "@/assets/svg/google_g.svg";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import WhimsicalBackground from "@/components/WhimsicalBackground";
import { useToast } from "@/hooks/use-toast";
import { api, authService, userStore, useUser } from "@/services/auth-service";
import { Badge, Bell, CreditCard, Loader2, Lock, LogOut, Mail, Shield } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AccountSettingsPage = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const user = useUser();
    const [isLoading, setIsLoading] = useState(false);

    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [currentPasswordError, setCurrentPasswordError] = useState("");
    const [newPasswordError, setNewPasswordError] = useState("");

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteEmail, setDeleteEmail] = useState("");
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const [showGoogleDisconnectDialog, setShowGoogleDisconnectDialog] = useState(false);
    const [googleActionLoading, setGoogleActionLoading] = useState(false);
    const [googleError, setGoogleError] = useState("");

    const handleEmailUpdate = async (e) => {
        e.preventDefault();
        setEmailError("");
        setIsLoading(true);

        try {
            await api.patch('/account/email/', { email: newEmail });
            userStore.setUser({ ...user, email: newEmail });
            toast({
                title: "Email Updated",
                description: "Your email has been successfully updated.",
            });
            setIsEditingEmail(false);
            setNewEmail("");
        } catch (error) {
            // @ts-expect-error ignoring
            if (error.response?.data?.error?.includes('Email already in use')) {
                setEmailError("This email is already in use.");
            } else {
                setEmailError("Failed to update email. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        setCurrentPasswordError("");
        setNewPasswordError("");

        if (newPassword.length < 8) {
            setNewPasswordError("New password must be at least 8 characters long");
            return;
        }

        setIsLoading(true);

        try {
            await api.patch('/account/password/', {
                current_password: currentPassword,
                new_password: newPassword
            });
            toast({
                title: "Password Updated",
                description: "Your password has been successfully updated.",
            });
            setIsEditingPassword(false);
            setCurrentPassword("");
            setNewPassword("");
        } catch (error) {
            // @ts-expect-error ignoring
            if (error.response?.data?.error === 'Current password is incorrect') {
                setCurrentPasswordError("Current password is incorrect");
            } else {
                setNewPasswordError("Failed to update password. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordSet = async (e) => {
        e.preventDefault();
        setNewPasswordError("");

        if (newPassword.length < 8) {
            setNewPasswordError("Password must be at least 8 characters long");
            return;
        }

        setIsLoading(true);

        try {
            await api.post('/account/password/', { password: newPassword });
            userStore.setUser({ ...user, has_password: true });
            toast({
                title: "Password Set",
                description: "Your password has been successfully set.",
            });
            setIsEditingPassword(false);
            setNewPassword("");
        } catch (error) {
            console.error(error);
            setNewPasswordError("Failed to set password. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleMarketingPreference = async (checked) => {
        setIsLoading(true);
        try {
            await api.patch('/account/marketing/', { enabled: checked });
            userStore.setUser({ ...user, marketing_emails_enabled: checked });
            toast({
                title: "Preferences Updated",
                description: "Your marketing preferences have been updated.",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "Update Failed",
                description: "Failed to update marketing preferences.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setIsLoading(true);
        try {
            await api.post('/account/verify/resend/');
            toast({
                title: "Verification Email Sent",
                description: "A new verification email has been sent to your inbox.",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "Failed to Send",
                description: "Could not send verification email. Please try again later.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete('/account/delete/', { data: { email: user.email } });
            toast({
                title: "Account Deleted",
                description: "Your account has been successfully deleted.",
            });
            userStore.clearUser();
            navigate('/');
        } catch (error) {
            console.error(error);
            toast({
                title: "Error",
                description: "Failed to delete account. Please try again.",
                variant: "destructive",
            });
        }
        setShowDeleteDialog(false);
        setShowConfirmDialog(false);
    };

    const handleGoogleDisconnect = async () => {
        setGoogleActionLoading(true);
        setGoogleError("");

        try {
            const response = await api.post('/account/social/google/disconnect/');

            if (response.status === 200) {
                userStore.setUser({ ...user, is_google_connected: false });
                toast({
                    title: "Success",
                    description: "Successfully disconnected from Google",
                });
            }
        } catch (error) {
            // @ts-expect-error ignoring
            if (error.response?.data?.code === 'no_password') {
                setGoogleError("You must set a password before disconnecting from Google");
            // @ts-expect-error ignoring
            } else if (error.response?.data?.error) {
                // @ts-expect-error ignoring
                setGoogleError(error.response.data.error);
            } else {
                setGoogleError("Failed to disconnect from Google. Please try again.");
            }
        } finally {
            setGoogleActionLoading(false);
            setShowGoogleDisconnectDialog(false);
        }
    };

    const handle2FAToggle = () => {
        // setUser({ ...user, two_factor_enabled: checked });
    };

    const getStripePortalUrl = async () => {
        try {
            const response = await api.post('/payments/create-portal-session/')
            return response.data.portal_url;
        } catch (error) {
          console.error('Error getting portal URL:', error);
          return null;
        }
      };

      const handleSubscriptionButtonClick = async () => {
        if (details.isExternal) {
            const url = await getStripePortalUrl();
            if (url) {
                window.open(url, '_blank');
            }
        } else {
            navigate(details.buttonHref);
        }
    };

    const getSubscriptionDetails = (user) => {
        if (!user) return null;

        const { plan, subscription_status, subscription_type } = user;

        const getPlanLabel = () => {
            switch (plan) {
                case 'FAMILY': return 'Family Plan';
                case 'FAMILY_PRO': return 'Family Pro Plan';
                case 'FREE': return 'Free Plan';
                default: return 'Unknown Plan';
            }
        };

        const getSubscriptionLabel = () => {
            if (subscription_type === 'YEARLY') return 'Yearly';
            if (subscription_type === 'MONTHLY') return 'Monthly';
            return '';
        };

        switch (subscription_status) {
            case 'ACTIVE':
                return {
                    title: `Active ${getPlanLabel()}`,
                    description: `Your ${getSubscriptionLabel()} subscription is active`,
                    statusBadge: { label: 'Active', color: '#1ec851' },
                    buttonText: 'Manage Subscription',
                    buttonVariant: 'outline' as const,
                    isExternal: true
                };
            case 'TRIAL':
                return {
                    title: `${getPlanLabel()} - Trial`,
                    description: 'Your trial period is currently active',
                    statusBadge: { label: 'Trial', color: '#0d6ec9' },
                    buttonText: 'Manage Trial',
                    buttonVariant: 'default' as const,
                    isExternal: true
                };
            case 'PAST_DUE':
                return {
                    title: `Past Due - ${getPlanLabel()}`,
                    description: 'Your payment is overdue. Please update your payment method.',
                    statusBadge: { label: 'Past Due', color: '#da1616' },
                    buttonText: 'Update Payment',
                    buttonVariant: 'destructive' as const,
                    isExternal: true
                };
            case 'CANCELED':
                return {
                    title: 'Subscription Canceled',
                    description: 'Your subscription will end at the end of the billing period',
                    statusBadge: { label: 'Canceled', color: '#3e454c' },
                    buttonText: 'Reactivate',
                    buttonVariant: 'default' as const,
                    buttonHref: '/plans'
                };
            case 'NONE':
            default:
                return {
                    title: 'No Active Subscription',
                    description: 'Subscribe to access premium features',
                    statusBadge: { label: 'Free', color: '#3e454c' },
                    buttonText: 'Subscribe Now',
                    buttonVariant: 'default' as const,
                    buttonHref: '/plans'
                };
        }
    };

    const details = getSubscriptionDetails(user);

    return (
        <div className="max-w-3xl p-4 mx-auto space-y-6">
            <WhimsicalBackground />

            <h1 className="text-3xl font-bold">Account Settings</h1>

            {/* Email Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Email Settings
                    </CardTitle>
                    <CardDescription>Manage your email address and verification status</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Separator />
                    {!isEditingEmail ? (
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Current Email</p>
                                <div className="md:space-x-2 items-c enter md:flex">
                                    <p className="text-sm text-slate-700 dark:text-slate-100">{user?.email}</p>
                                    {user.email_verified ? (
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-200 rounded-full text-slate-800">
                                            Verified
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-200 rounded-full text-slate-800">
                                            Unverified
                                        </span>
                                    )}
                                </div>
                                {!user.email_verified && (
                                    <Button variant="link"
                                            className="h-auto p-0 text-sm"
                                            onClick={handleResendVerification}
                                            disabled={isLoading}
                                    >
                                        Resend verification email
                                    </Button>
                                )}
                            </div>
                            <Button onClick={() => setIsEditingEmail(true)} className={"mt-2"}>Change Email</Button>
                        </div>
                    ) : (
                        <form onSubmit={handleEmailUpdate} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="newEmail" className={"pl-1"}>New Email Address</Label>
                                <Input
                                    id="newEmail"
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    required
                                />
                                {emailError && (
                                    <p className="text-sm text-destructive">{emailError}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsEditingEmail(false)}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit">Save Changes</Button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* Password Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        Password & Authentication
                    </CardTitle>
                    <CardDescription>Manage your password and sign-in methods</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Separator />
                    {user.has_password ? (
                        !isEditingPassword ? (
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <p className="text-sm font-medium">Password</p>
                                    <p className="text-sm text-slate-700 dark:text-slate-100">••••••••</p>
                                </div>
                                <Button
                                    onClick={() => setIsEditingPassword(true)}
                                    disabled={isLoading}
                                >
                                    Change Password
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handlePasswordUpdate} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="currentPassword">Current Password</Label>
                                    <Input
                                        id="currentPassword"
                                        type="password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        required
                                    />
                                    {currentPasswordError && (
                                        <p className="text-sm text-destructive">{currentPasswordError}</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="newPassword">New Password</Label>
                                    <Input
                                        id="newPassword"
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                    {newPasswordError && (
                                        <p className="text-sm text-destructive">{newPasswordError}</p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setIsEditingPassword(false);
                                            setCurrentPasswordError("");
                                            setNewPasswordError("");
                                        }}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={isLoading}>
                                        Update Password
                                    </Button>
                                </div>
                            </form>
                        )
                    ) : (
                        !isEditingPassword ? (
                            <div className="space-y-2">
                                <Alert>
                                    <AlertDescription className="flex items-center justify-between max-w-[29rem]">
                                        You signed up with Google. Set a password to enable password login.
                                        <Button onClick={() => setIsEditingPassword(true)} className="ml-2">
                                            Set Password
                                        </Button>
                                    </AlertDescription>
                                </Alert>
                            </div>
                        ) : (
                            <form onSubmit={handlePasswordSet} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="newPassword">Password</Label>
                                    <Input
                                        id="newPassword"
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                    {newPasswordError && (
                                        <p className="text-sm text-destructive">{newPasswordError}</p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setIsEditingPassword(false);
                                            setNewPasswordError("");
                                        }}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={isLoading}>
                                        Set Password
                                    </Button>
                                </div>
                            </form>
                        )
                    )}

                    <Separator />

                    {/* Google Connection */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Google Account</p>
                                <p className="text-sm text-muted-foreground">
                                    {user.is_google_connected
                                        ? "Connected to Google"
                                        : "Not connected to Google"}
                                </p>
                                {user.is_google_connected && !user.has_password && (
                                    <p className="text-sm text-red-700 dark:text-red-500">
                                        Set a password to be able to disconnect from Google
                                    </p>
                                )}
                            </div>
                            <Button
                                variant="outline"
                                onClick={user.is_google_connected
                                    ? () => setShowGoogleDisconnectDialog(true)
                                    : authService.handleGoogleAuth
                                }
                                disabled={
                                    (user.is_google_connected && !user.has_password) ||
                                    googleActionLoading
                                }
                                className="min-w-[120px] flex items-center justify-center gap-2"
                            >
                                {googleActionLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : !user.is_google_connected ? (
                                    <>
                                        <img
                                            src={googleSvg}
                                            alt="Google Icon"
                                            className="w-4 h-4"
                                        />
                                        Connect
                                    </>
                                ) : (
                                    "Disconnect"
                                )}
                            </Button>
                        </div>

                        {googleError && (
                            <Alert variant="destructive">
                                <AlertDescription>{googleError}</AlertDescription>
                            </Alert>
                        )}

                        <AlertDialog open={showGoogleDisconnectDialog} onOpenChange={setShowGoogleDisconnectDialog}>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Disconnect Google Account</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Are you sure you want to disconnect your Google account? You'll still be able to log in using your email and password.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={handleGoogleDisconnect}
                                        disabled={googleActionLoading}
                                    >
                                        {googleActionLoading ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : null}
                                        Disconnect
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>

                    <Separator />

                    {/* Two-Factor Authentication */}
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-sm font-medium">Two-Factor Authentication</p>
                            <p className="text-sm text-slate-700 dark:text-slate-100">
                                Coming soon
                            </p>
                        </div>
                        <Switch
                            checked={user.two_factor_enabled}
                            onCheckedChange={handle2FAToggle}
                            disabled={true}
                            aria-label="Enable Two-Factor Authentication"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Preferences Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Bell className="w-5 h-5" />
                        Communication Preferences
                    </CardTitle>
                    <CardDescription>Manage your email notifications and marketing preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Separator />
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-sm font-medium">Marketing Emails</p>
                            <p className="text-sm text-slate-700 dark:text-slate-100">
                                Receive emails about new features and special offers
                            </p>
                        </div>
                        <Switch
                            checked={user.marketing_emails_enabled}
                            onCheckedChange={handleMarketingPreference}
                            disabled={isLoading}
                            aria-label="Toggle Marketing Emails"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Subscription Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Subscription Management
                    </CardTitle>
                    <CardDescription>Manage your subscription and billing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Separator />
                    <div className="flex items-center justify-between">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{details.title}</p>
                                <Badge color={details.statusBadge.color}>
                                    {details.statusBadge.label}
                                </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{details.description}</p>
                            {user.subscription_type && (
                                <p className="text-xs text-muted-foreground">
                                    Billing cycle: {user.subscription_type.charAt(0) + user.subscription_type.substring(1).toLowerCase()}
                                </p>
                            )}
                        </div>
                        <Button
                            onClick={handleSubscriptionButtonClick}
                            variant={details.buttonVariant}
                        >
                            {details.buttonText}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Account Actions */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-500">
                        <Shield className="w-5 h-5" />
                        Account Actions
                    </CardTitle>
                    <CardDescription>Manage your account status</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Separator />
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-sm font-medium">Sign Out</p>
                            <p className="text-sm text-slate-700 dark:text-slate-100">
                                Sign out of your account on this device
                            </p>
                        </div>
                        <Button variant="outline"
                                className="gap-2"
                                onClick={() => authService.logout()}>
                            <LogOut className="w-4 h-4" />
                            Sign Out
                        </Button>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <p className="text-sm font-medium">Delete Account</p>
                            <p className="text-sm text-slate-700 dark:text-slate-100">
                                Permanently delete your account and all associated data
                            </p>
                        </div>
                        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                            <DialogTrigger asChild>
                                <Button variant="destructive" className="mt-2 ml-2">Delete Account</Button>
                            </DialogTrigger>
                            {!showConfirmDialog ? (
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Delete Account</DialogTitle>
                                        <DialogDescription>
                                            Permanently delete your account and all associated data? This action cannot be undone.
                                            <br/><br/> Please enter your email address to confirm.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="py-4 space-y-4">
                                        <div className="space-y-2">
                                            <Label>Email Address</Label>
                                            <Input
                                                type="email"
                                                value={deleteEmail}
                                                onChange={(e) => setDeleteEmail(e.target.value)}
                                                placeholder={user.email}
                                            />
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button
                                            variant="outline"
                                            className={"mt-0 max-sm:mt-2"}
                                            onClick={() => {
                                                setShowDeleteDialog(false);
                                                setDeleteEmail("");
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            onClick={() => setShowConfirmDialog(true)}
                                            disabled={deleteEmail !== user.email}
                                        >
                                            Continue
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            ) : (
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Confirm Deletion</DialogTitle>
                                        <DialogDescription>
                                            Are you sure you want to delete your account? This action is permanent and cannot be undone.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogFooter className={"mt-6"}>
                                        <Button
                                            variant="outline"
                                            className={"mt-0 max-sm:mt-2"}
                                            onClick={() => {
                                                setShowConfirmDialog(false);
                                                setDeleteEmail("");
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            onClick={handleDelete}
                                        >
                                            Delete Account
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            )}
                        </Dialog>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AccountSettingsPage;
